import { useQuery } from '@apollo/client';
import {
  GET_TOTAL_EVENTS_TRACKED,
  GET_CONVERSION_MONITORING_METRICS,
  GET_ANON_VISITORS_CAPTURED,
  GET_LIFECYCLE_REVENUE,
  GET_LIFETIME_REVENUE,
  GET_LIFETIME_ROI,
  GET_FLOWS_REVENUE,
  GET_METRICS_COMPARISON,
  GET_EMQ_SCORES_BY_DATE_RANGE,
  GET_EM_SCORE_AVERAGE_AND_TREND,
  GET_EMQ_SCORE_DEFAULT_AND_AVERAGE
} from '@/queries/metrics';
import { useMemo } from 'react';
import {
  PURCHASE_CONVERSION_RATE_LABEL,
  ANON_VISITORS_CAPTURED_LABEL,
  transformMetricToTimeSeries,
  combineTwoSeriesByDate,
  calculateAverageForPurchaseConversionRate,
  formatNumber,
  getTotalEventsTrackedMetric,
  formatDate
} from '@/utils/dashboard-utils';
import { formatCurrency } from '@/utils/format-utils';
import { METRIC_TYPES } from '@/routes/dashboards/config/chart-config';
import type {
  GetTotalEventsTrackedQuery,
  GetTotalEventsTrackedQueryVariables,
  GetConversionMonitoringMetricsQuery,
  GetConversionMonitoringMetricsQueryVariables,
  GetAnonVisitorsCapturedQuery,
  GetAnonVisitorsCapturedQueryVariables,
  GetLifecycleRevenueQuery,
  GetLifecycleRevenueQueryVariables,
  GetLifetimeRevenueQuery,
  GetLifetimeRevenueQueryVariables,
  GetLifetimeRoiQuery,
  GetLifetimeRoiQueryVariables,
  GetFlowsRevenueQuery,
  GetFlowsRevenueQueryVariables,
  GetMetricsComparisonQuery,
  GetMetricsComparisonQueryVariables,
  GetEmqScoresByDateRangeQuery,
  GetEmqScoresByDateRangeQueryVariables,
  GetEmqScoreAverageAndTrendQuery,
  GetEmqScoreAverageAndTrendQueryVariables,
  GetEmqScoreDefaultAndAverageQuery,
  GetEmqScoreDefaultAndAverageQueryVariables
} from '@/gql/graphql';
import { MetricComparisonType } from '@/gql/graphql';

interface DashboardMetricsOptions {
  merchantId: string;
  fromDate: string;
  toDate: string;
}

export function useDashboardMetrics({ merchantId, fromDate, toDate }: DashboardMetricsOptions) {
  const emqScoreDefaultAndAverage = useQuery<
    GetEmqScoreDefaultAndAverageQuery,
    GetEmqScoreDefaultAndAverageQueryVariables
  >(GET_EMQ_SCORE_DEFAULT_AND_AVERAGE, {
    variables: {
      startDate: fromDate,
      endDate: toDate
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });
  const emqScoreAverageAndTrend = useQuery<
    GetEmqScoreAverageAndTrendQuery,
    GetEmqScoreAverageAndTrendQueryVariables
  >(GET_EM_SCORE_AVERAGE_AND_TREND, {
    variables: {
      startDate: fromDate,
      endDate: toDate
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const totalEmqScores = useQuery<
    GetEmqScoresByDateRangeQuery,
    GetEmqScoresByDateRangeQueryVariables
  >(GET_EMQ_SCORES_BY_DATE_RANGE, {
    variables: {
      startDate: fromDate,
      endDate: toDate
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const totalEvents = useQuery<GetTotalEventsTrackedQuery, GetTotalEventsTrackedQueryVariables>(
    GET_TOTAL_EVENTS_TRACKED,
    {
      variables: {
        merchantId,
        fromDate,
        toDate
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

  const conversionMetrics = useQuery<
    GetConversionMonitoringMetricsQuery,
    GetConversionMonitoringMetricsQueryVariables
  >(GET_CONVERSION_MONITORING_METRICS, {
    variables: {
      merchantId,
      fromDate,
      toDate
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const anonVisitors = useQuery<
    GetAnonVisitorsCapturedQuery,
    GetAnonVisitorsCapturedQueryVariables
  >(GET_ANON_VISITORS_CAPTURED, {
    variables: {
      merchantId,
      fromDate,
      toDate
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const lifecycleRevenue = useQuery<GetLifecycleRevenueQuery, GetLifecycleRevenueQueryVariables>(
    GET_LIFECYCLE_REVENUE,
    {
      variables: {
        merchantId,
        fromDate,
        toDate
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

  // Note: Lifetime queries don't need date range
  const lifetimeRevenue = useQuery<GetLifetimeRevenueQuery, GetLifetimeRevenueQueryVariables>(
    GET_LIFETIME_REVENUE,
    {
      variables: {
        merchantId
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

  const lifetimeRoi = useQuery<GetLifetimeRoiQuery, GetLifetimeRoiQueryVariables>(
    GET_LIFETIME_ROI,
    {
      variables: {
        merchantId
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

  const flowsRevenue = useQuery<GetFlowsRevenueQuery, GetFlowsRevenueQueryVariables>(
    GET_FLOWS_REVENUE,
    {
      variables: {
        merchantId,
        fromDate,
        toDate
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

  const totalEventsComparison = useQuery<
    GetMetricsComparisonQuery,
    GetMetricsComparisonQueryVariables
  >(GET_METRICS_COMPARISON, {
    variables: {
      merchantId,
      fromDate,
      toDate,
      metricType: MetricComparisonType.TotalEvents
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const anonVisitorsComparison = useQuery<
    GetMetricsComparisonQuery,
    GetMetricsComparisonQueryVariables
  >(GET_METRICS_COMPARISON, {
    variables: {
      merchantId,
      fromDate,
      toDate,
      metricType: MetricComparisonType.AnonVisitors
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const revenueComparison = useQuery<GetMetricsComparisonQuery, GetMetricsComparisonQueryVariables>(
    GET_METRICS_COMPARISON,
    {
      variables: {
        merchantId,
        fromDate,
        toDate,
        metricType: MetricComparisonType.Revenue
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

  const roiComparison = useQuery<GetMetricsComparisonQuery, GetMetricsComparisonQueryVariables>(
    GET_METRICS_COMPARISON,
    {
      variables: {
        merchantId,
        fromDate,
        toDate,
        metricType: MetricComparisonType.Roi
      },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

  // Derived metrics calculations
  const conversion = useMemo(() => {
    try {
      const timeSeries = conversionMetrics.data?.getConversionMonitoringMetrics
        ? transformMetricToTimeSeries({
            metric: conversionMetrics.data.getConversionMonitoringMetrics,
            label: PURCHASE_CONVERSION_RATE_LABEL
          })
        : [];

      const values = timeSeries.map((data) => data[PURCHASE_CONVERSION_RATE_LABEL] ?? 0);
      const average = calculateAverageForPurchaseConversionRate(values);

      return {
        timeSeries,
        rate: values.length > 0 ? average : '-'
      };
    } catch (error) {
      console.error('Error calculating conversion metrics:', error);
      return {
        timeSeries: [],
        rate: '-'
      };
    }
  }, [conversionMetrics.data?.getConversionMonitoringMetrics]);

  const anonymousVisitors = useMemo(() => {
    try {
      const timeSeries = anonVisitors.data?.getAnonVisitorsCaptured
        ? transformMetricToTimeSeries({
            metric: anonVisitors.data.getAnonVisitorsCaptured,
            label: ANON_VISITORS_CAPTURED_LABEL
          })
        : [];

      const total =
        anonVisitors.data?.getAnonVisitorsCaptured.aggregate !== undefined
          ? formatNumber(anonVisitors.data.getAnonVisitorsCaptured.aggregate)
          : '-';

      return {
        timeSeries,
        total
      };
    } catch (error) {
      console.error('Error calculating anonymous visitors metrics:', error);
      return {
        timeSeries: [],
        total: '-'
      };
    }
  }, [anonVisitors.data?.getAnonVisitorsCaptured]);

  const events = useMemo(() => {
    try {
      if (!totalEvents.data?.getTotalEventsTracked.length) {
        return {
          total: '-',
          timeSeries: []
        };
      }

      return getTotalEventsTrackedMetric(totalEvents.data.getTotalEventsTracked);
    } catch (error) {
      console.error('Error calculating events metrics:', error);
      return {
        total: '-',
        timeSeries: []
      };
    }
  }, [totalEvents.data?.getTotalEventsTracked]);

  const revenue = useMemo(() => {
    try {
      if (!lifecycleRevenue.data?.getLifecycleRevenue || !flowsRevenue.data?.getFlowsRevenue) {
        return {
          total: '-',
          timeSeries: []
        };
      }

      const total =
        lifecycleRevenue.data.getLifecycleRevenue.aggregate +
        flowsRevenue.data.getFlowsRevenue.aggregate;

      // Build individual series using the shared helper to ensure UTC handling & trimming
      const lifecycleSeries = transformMetricToTimeSeries({
        metric: lifecycleRevenue.data.getLifecycleRevenue,
        label: METRIC_TYPES.LIFECYCLE_REVENUE,
        fromDate,
        toDate
      });

      const flowsSeries = transformMetricToTimeSeries({
        metric: flowsRevenue.data.getFlowsRevenue,
        label: METRIC_TYPES.FLOWS_REVENUE,
        fromDate,
        toDate
      });

      const combinedTimeSeries = combineTwoSeriesByDate(
        lifecycleSeries,
        flowsSeries,
        METRIC_TYPES.LIFECYCLE_REVENUE,
        METRIC_TYPES.FLOWS_REVENUE
      );

      return {
        total: formatCurrency(total),
        timeSeries: combinedTimeSeries
      };
    } catch (error) {
      console.error('Error calculating revenue metrics:', error);
      return {
        total: '-',
        timeSeries: []
      };
    }
  }, [
    lifecycleRevenue.data?.getLifecycleRevenue,
    flowsRevenue.data?.getFlowsRevenue,
    fromDate,
    toDate
  ]);

  const roi = useMemo(() => {
    try {
      const currentPeriod = roiComparison.data?.getMetricsComparison.currentPeriod;
      if (!currentPeriod?.aggregate) {
        return {
          total: '-',
          timeSeries: []
        };
      }

      const timeSeries = transformMetricToTimeSeries({
        metric: currentPeriod,
        label: METRIC_TYPES.TOTAL_ROI,
        fromDate,
        toDate
      });

      return {
        total: `${currentPeriod.aggregate.toFixed(1)}x`,
        timeSeries
      };
    } catch (error) {
      console.error('Error calculating ROI metrics:', error);
      return {
        total: '-',
        timeSeries: []
      };
    }
  }, [roiComparison.data?.getMetricsComparison.currentPeriod, fromDate, toDate]);

  // Transform EMQ scores into time series format
  const emqScores = useMemo(() => {
    if (!totalEmqScores.data?.getEMQScoresByDateRange) {
      return [];
    }

    return totalEmqScores.data.getEMQScoresByDateRange.map((score) => {
      const { short } = formatDate(score.date);

      return {
        date: short,
        [METRIC_TYPES.META_PAGE_VIEW_EMQ_SCORE]: score.META_PAGE_VIEW_EMQ_SCORE ?? undefined,
        [METRIC_TYPES.META_VIEW_CONTENT_EMQ_SCORE]: score.META_VIEW_CONTENT_EMQ_SCORE ?? undefined,
        [METRIC_TYPES.META_ADD_TO_CART_EMQ_SCORE]: score.META_ADD_TO_CART_EMQ_SCORE ?? undefined,
        [METRIC_TYPES.META_INITIATE_CHECKOUT_EMQ_SCORE]:
          score.META_INITIATE_CHECKOUT_EMQ_SCORE ?? undefined,
        [METRIC_TYPES.META_ADD_PAYMENT_INFO_EMQ_SCORE]:
          score.META_ADD_PAYMENT_INFO_EMQ_SCORE ?? undefined,
        [METRIC_TYPES.META_PURCHASE_EMQ_SCORE]: score.META_PURCHASE_EMQ_SCORE ?? undefined,
        [METRIC_TYPES.META_LEAD_EMQ_SCORE]: score.META_LEAD_EMQ_SCORE ?? undefined
      };
    });
  }, [totalEmqScores.data?.getEMQScoresByDateRange]);

  return {
    totalEvents,
    conversionMetrics,
    anonVisitors,
    lifecycleRevenue,
    lifetimeRevenue,
    lifetimeRoi,
    flowsRevenue,
    totalEventsComparison,
    anonVisitorsComparison,
    revenueComparison,
    roiComparison,
    totalEmqScores,
    emqScoreAverageAndTrend,
    emqScoreDefaultAndAverage,
    // Derived metrics
    metrics: {
      conversion,
      anonymousVisitors,
      events,
      revenue,
      roi,
      emqScores
    }
  };
}
